// @ts-check

import {
  cartFetchAndRender,
  maybeOpenSideCartAfterChange,
} from '@theme/lib-cart';

/** @type {WeakSet<HTMLFormElement>} */
var inFlightForms = new WeakSet();

/**
 * @returns {Set<number>}
 */
function getBusyLinesStore() {
  var windowWithStore =
    /** @type {Window & { __sideCartBusyLines?: Set<number> }} */ (window);
  if (!windowWithStore.__sideCartBusyLines) {
    windowWithStore.__sideCartBusyLines = new Set();
  }
  return windowWithStore.__sideCartBusyLines;
}

/**
 * @param {number} line
 */
function isLineBusy(line) {
  return getBusyLinesStore().has(line);
}

/**
 * @param {number} line
 * @param {boolean} isBusy
 */
function setLineBusyState(line, isBusy) {
  var lineItems = document.querySelectorAll(
    '.side-cart__item[data-line="' + line + '"]'
  );
  if (!lineItems.length) return;

  lineItems.forEach(function (lineItem) {
    if (!(lineItem instanceof HTMLElement)) return;

    lineItem.classList.toggle('is-loading', isBusy);
    if (isBusy) {
      lineItem.setAttribute('aria-busy', 'true');
    } else {
      lineItem.removeAttribute('aria-busy');
    }

    var controls = lineItem.querySelectorAll(
      '.quantity-controls-form button[type="submit"], .delete-button-form .delete-button, .move-to-wishlist-button'
    );
    controls.forEach(function (control) {
      if (!(control instanceof HTMLButtonElement)) return;

      if (isBusy) {
        control.dataset.wasDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        return;
      }

      if (control.dataset.wasDisabled === 'true') {
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      } else {
        control.disabled = false;
        control.removeAttribute('aria-disabled');
      }
      delete control.dataset.wasDisabled;
    });
  });
}

/**
 * @param {HTMLFormElement} form
 */
async function submitQuantityForm(form) {
  if (inFlightForms.has(form)) return;

  var lineInput = form.querySelector('input[name="line"]');
  var quantityInput = form.querySelector('input[name="quantity"]');
  if (!lineInput || !quantityInput) return;

  var line = parseInt(/** @type {HTMLInputElement} */ (lineInput).value, 10);
  var quantity = parseInt(
    /** @type {HTMLInputElement} */ (quantityInput).value,
    10
  );
  if (Number.isNaN(line) || Number.isNaN(quantity)) return;
  if (isLineBusy(line)) return;

  var busyLines = getBusyLinesStore();
  busyLines.add(line);
  setLineBusyState(line, true);

  inFlightForms.add(form);

  try {
    await cartFetchAndRender(
      'cart/change.js',
      {
        line: line,
        quantity: quantity,
      },
      {
        action: 'update',
        context: 'cart',
        lines: [{ id: line, quantity }],
      }
    );
    maybeOpenSideCartAfterChange(form);
  } catch (error) {
    console.error('Quantity update failed:', error);
  } finally {
    busyLines.delete(line);
    setLineBusyState(line, false);
    inFlightForms.delete(form);
  }
}

/**
 * Intercepts quantity-control form submissions, sends the change via the
 * Cart Ajax API with Section Rendering, and updates the side cart in-place
 * instead of triggering a full-page reload.
 */
document.addEventListener(
  'click',
  function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var button = target.closest(
      '.quantity-controls-form button[type="submit"]'
    );
    if (!(button instanceof HTMLButtonElement)) return;

    var form = button.form;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('quantity-controls-form')) return;

    event.preventDefault();
    event.stopPropagation();
    submitQuantityForm(form);
  },
  true
);

document.addEventListener(
  'submit',
  function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('quantity-controls-form')) return;

    event.preventDefault();
    event.stopPropagation();
    submitQuantityForm(form);
  },
  true
);
